(() => {
  if (window.AuraGleamProductStory) {
    window.AuraGleamProductStory.init();
    return;
  }

  const SELECTORS = {
    rail: '[data-ag-rail]',
    item: '[data-ag-rail-item]',
    previous: '[data-ag-prev]',
    next: '[data-ag-next]',
    dots: '[data-ag-dots]',
    lazyVideo: 'video[data-ag-load-mode="lazy"]',
    interactionVideo: 'video[data-ag-load-mode="interaction"]',
    play: '[data-ag-play]',
    faq: '[data-ag-faq]',
  };

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const hydrateVideo = (video) => {
    if (!video || video.dataset.agHydrated === 'true') return;

    video.querySelectorAll('source[data-src]').forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    });

    if (video.dataset.src) {
      video.src = video.dataset.src;
      video.removeAttribute('data-src');
    }

    video.dataset.agHydrated = 'true';
    video.load();
  };

  const initLazyVideos = (scope) => {
    const videos = [...scope.querySelectorAll(SELECTORS.lazyVideo)].filter(
      (video) => video.dataset.agObserved !== 'true'
    );

    if (!videos.length) return;

    if (!('IntersectionObserver' in window)) {
      videos.forEach(hydrateVideo);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          hydrateVideo(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '280px 0px' }
    );

    videos.forEach((video) => {
      video.dataset.agObserved = 'true';
      observer.observe(video);
    });
  };

  const getActiveIndex = (rail, items) => {
    const railRect = rail.getBoundingClientRect();
    const centered = rail.dataset.agCentered === 'true';
    const reference = centered
      ? railRect.left + railRect.width / 2
      : railRect.left + Math.min(railRect.width * 0.36, 260);
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    items.forEach((item, index) => {
      const itemRect = item.getBoundingClientRect();
      const itemReference = centered ? itemRect.left + itemRect.width / 2 : itemRect.left;
      const distance = Math.abs(itemReference - reference);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  };

  const scrollToItem = (rail, items, index, behaviorOverride) => {
    const target = items[Math.max(0, Math.min(index, items.length - 1))];
    if (!target) return;
    const centered = rail.dataset.agCentered === 'true';
    const targetLeft = centered
      ? target.offsetLeft - items[0].offsetLeft
      : target.offsetLeft - rail.offsetLeft;
    if (behaviorOverride === 'instant') {
      const previousSnap = rail.style.scrollSnapType;
      const previousBehavior = rail.style.scrollBehavior;
      rail.style.scrollSnapType = 'none';
      rail.style.scrollBehavior = 'auto';
      rail.scrollLeft = targetLeft;
      window.requestAnimationFrame(() => {
        rail.style.scrollSnapType = previousSnap;
        rail.style.scrollBehavior = previousBehavior;
      });
      return;
    }

    rail.scrollTo({
      left: targetLeft,
      behavior: behaviorOverride || (prefersReducedMotion ? 'auto' : 'smooth'),
    });
  };

  const initRail = (rail) => {
    if (rail.dataset.agInitialized === 'true') return;

    const shell = rail.closest('[data-ag-rail-shell]') || rail.parentElement;
    const items = [...rail.querySelectorAll(SELECTORS.item)];
    const previous = shell.querySelector(SELECTORS.previous);
    const next = shell.querySelector(SELECTORS.next);
    const dots = shell.querySelector(SELECTORS.dots);
    const wrap = rail.dataset.agWrap === 'true';
    const autoplaySeconds = Number(rail.dataset.agAutoplay || 0);
    const startIndex = Math.max(0, Math.min(Number(rail.dataset.agStartIndex || 0), items.length - 1));
    let activeIndex = startIndex;
    let scrollFrame;
    let autoplayTimer;

    if (!items.length) return;
    rail.dataset.agInitialized = 'true';

    const dotButtons = items.map((_, index) => {
      if (!dots) return null;
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'ag-story__dot';
      dot.setAttribute('aria-label', `Go to item ${index + 1}`);
      dot.addEventListener('click', () => scrollToItem(rail, items, index));
      dots.appendChild(dot);
      return dot;
    });

    const update = () => {
      activeIndex = getActiveIndex(rail, items);
      dotButtons.forEach((dot, index) => {
        if (dot) dot.setAttribute('aria-current', index === activeIndex ? 'true' : 'false');
      });
      items.forEach((item, index) => {
        item.classList.toggle('is-active', index === activeIndex);
        item.setAttribute('aria-current', index === activeIndex ? 'true' : 'false');
      });

      if (previous) previous.disabled = !wrap && activeIndex === 0;
      if (next) next.disabled = !wrap && activeIndex === items.length - 1;
    };

    const go = (direction) => {
      let target = activeIndex + direction;
      if (wrap) target = (target + items.length) % items.length;
      scrollToItem(rail, items, target);
    };

    previous?.addEventListener('click', () => go(-1));
    next?.addEventListener('click', () => go(1));

    rail.addEventListener(
      'scroll',
      () => {
        window.cancelAnimationFrame(scrollFrame);
        scrollFrame = window.requestAnimationFrame(update);
      },
      { passive: true }
    );

    const stopAutoplay = () => window.clearInterval(autoplayTimer);
    const startAutoplay = () => {
      if (prefersReducedMotion || autoplaySeconds <= 0 || items.length < 2) return;
      stopAutoplay();
      autoplayTimer = window.setInterval(() => go(1), autoplaySeconds * 1000);
    };

    shell.addEventListener('mouseenter', stopAutoplay);
    shell.addEventListener('mouseleave', startAutoplay);
    shell.addEventListener('focusin', stopAutoplay);
    shell.addEventListener('focusout', startAutoplay);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stopAutoplay();
      else startAutoplay();
    });

    if (startIndex > 0) {
      scrollToItem(rail, items, startIndex, 'instant');
      window.requestAnimationFrame(update);
    } else {
      update();
    }
    startAutoplay();
  };

  const pauseOtherVideos = (current) => {
    document.querySelectorAll('[data-ag-video-card] video').forEach((video) => {
      if (video === current || video.paused) return;
      video.pause();
    });
  };

  const initVideoControls = (scope) => {
    scope.querySelectorAll(SELECTORS.play).forEach((button) => {
      if (button.dataset.agInitialized === 'true') return;
      button.dataset.agInitialized = 'true';

      const card = button.closest('[data-ag-video-card]');
      const video = card?.querySelector('video');
      if (!video) return;

      button.addEventListener('click', async () => {
        if (video.dataset.agLoadMode === 'interaction') hydrateVideo(video);
        pauseOtherVideos(video);
        video.controls = true;

        try {
          await video.play();
          card.classList.add('is-playing');
          button.setAttribute('aria-label', button.dataset.pauseLabel || 'Pause video');
        } catch (error) {
          card.classList.remove('is-playing');
        }
      });

      video.addEventListener('pause', () => card.classList.remove('is-playing'));
      video.addEventListener('ended', () => card.classList.remove('is-playing'));
      video.addEventListener('play', () => {
        pauseOtherVideos(video);
        card.classList.add('is-playing');
      });
    });
  };

  const initFaq = (faq) => {
    if (faq.dataset.agInitialized === 'true') return;
    faq.dataset.agInitialized = 'true';

    const tabs = [...faq.querySelectorAll('[data-ag-faq-tab]')];
    const items = [...faq.querySelectorAll('[data-ag-faq-item]')];
    const singleOpen = faq.dataset.agSingleOpen === 'true';

    const setTab = (tab) => {
      const category = tab.dataset.agFaqTab;
      tabs.forEach((candidate) => {
        const selected = candidate === tab;
        candidate.setAttribute('aria-selected', selected ? 'true' : 'false');
        candidate.tabIndex = selected ? 0 : -1;
      });
      items.forEach((item) => {
        item.hidden = item.dataset.agFaqItem !== category;
      });
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => setTab(tab));
      tab.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        let nextIndex = index;
        if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;
        tabs[nextIndex].focus();
        setTab(tabs[nextIndex]);
      });
    });

    items.forEach((item) => {
      const question = item.querySelector('[data-ag-faq-question]');
      const answer = item.querySelector('[data-ag-faq-answer]');
      if (!question || !answer) return;

      question.addEventListener('click', () => {
        const willOpen = question.getAttribute('aria-expanded') !== 'true';

        if (singleOpen && willOpen) {
          items.forEach((other) => {
            if (other === item) return;
            const otherQuestion = other.querySelector('[data-ag-faq-question]');
            const otherAnswer = other.querySelector('[data-ag-faq-answer]');
            otherQuestion?.setAttribute('aria-expanded', 'false');
            if (otherAnswer) otherAnswer.hidden = true;
          });
        }

        question.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
        answer.hidden = !willOpen;
      });
    });

    if (tabs[0]) setTab(tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0]);
  };

  const init = (scope = document) => {
    scope.querySelectorAll(SELECTORS.rail).forEach(initRail);
    scope.querySelectorAll(SELECTORS.faq).forEach(initFaq);
    initLazyVideos(scope);
    initVideoControls(scope);
  };

  window.AuraGleamProductStory = { init, hydrateVideo };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
